const PRODUCT_PROPERTIES = [
  'id',
  'title',
  'variants',
  'images',
  'bundles',
  'options',
  'swatches',
  'materials',
  'metafields',
  'priceMin',
  'discount',
]

const ProductState = (config) => ({
  eventKey: null,
  isPDP: false,
  isSimple: false,
  isLoading: false,
  isHydrated: null,
  id: null,
  handle: null,
  title: null,
  quantity: 1,
  variants: [],
  firstAvailableVariant: null,
  images: [],
  bundles: [],
  options: [],
  swatches: [],
  materials: [],
  metafields: {},
  selectedOptions: ['', '', ''],
  urlParams: {},
  atcBox: {},
  selectedBundle: null,
  validVariants: [],
  availableVariants: [],
  priceMin: null,
  shouldShowSwatchDropdown: true,
  discount: {
    percent: 0,
    usd: 0,
  },
  stagedAddons: [],
  stagedBundleDiscount: 0,
  stagedInBundle: false,

  __prev: {},
  whenChange(key, value, callback = () => {}) {
    if (Shopify.isDeepEqual(this.__prev[key], value)) return
    this.__prev[key] = value
    callback()
  },

  __cache: {},
  cache(key = '', value = null, dependencies = {}) {
    dependencies.isHydrated = this.isHydrated

    if (
      !this.__cache[key] ||
      !Shopify.isDeepEqual(dependencies, this.__cache[key].dependencies, key)
    ) {
      this.__cache[key] = {
        value: typeof value === 'function' ? value() : value,
        dependencies: Shopify.cloneDeep(dependencies),
      }
    }

    return this.__cache[key].value
  },

  toggleSwatchDropdown(forceOpen) {
    this.shouldShowSwatchDropdown = !this.shouldShowSwatchDropdown || forceOpen
  },

  get selectedVariantId() {
    const id = this.validVariants.find(
      (v) => v.options.join(',') === this.selectedOptions.join(',')
    )?.id

    this.whenChange('selectedVariantIdGet', id, () => {
      this.selectBundle()

      dispatch(`${this.eventKey ?? `product-${this.id}`}:variant-change`, this)

      if (id)
        this.updateURL({
          variant: id,
        })
    })

    return id
  },

  set selectedVariantId(id) {
    const selectedVariant =
      this.validVariants.find((v) => `${v.id}` === `${id}`) ??
      this.validVariants[0]
    this.selectedOptions = selectedVariant?.options
      ? [
          `${selectedVariant.options[0]}`,
          `${selectedVariant.options[1]}`,
          `${selectedVariant?.options[2]}`,
        ]
      : [null, null, null]
  },

  get selectedVariant() {
    return (
      this.validVariants.find(
        (v) => `${v.id}` === `${this.selectedVariantId}`
      ) ?? this.validVariants[0]
    )
  },

  get selectedOptionsWithNames() {
    return [0, 1, 2].map((i) => ({
      name: this.options[i]?.name,
      value: this.selectedOptions[i],
    }))
  },

  get optionsWithSwatches() {
    return this.cache(
      'optionsWithSwatches',
      () =>
        [...Array(this.options.length)].map((_, i) => {
          const optionValues = this.options[i].values
            .map((v) => {
              const options = [
                `${this.selectedOptions[0]}`,
                `${this.selectedOptions[1]}`,
                `${this.selectedOptions[2]}`,
              ]
              options[i] = v

              const variant =
                this.validVariants.find(
                  (_v) => _v.options.join(',') === options.join(',')
                ) ??
                this.validVariants.find((_v) => _v.options[i] === v) ??
                null
              const swatch =
                this.swatches.find(
                  (s) => s.optionIndex === i + 1 && s.value === v
                ) ?? null
              const bundle = !variant
                ? null
                : (this.getBundleWithSelectedVariant(
                    this.selectedBundle?.id,
                    variant?.options
                  ) ?? null)
              const price = !variant
                ? null
                : this.getFinalBundledPrice(variant, bundle)

              return {
                label: v,
                swatch,
                options,
                variant,
                price,
                bundle,
                offsetPrice: price * this.quantity - this.totalFinalPrice,
              }
            })
            .filter(
              (optionValue) =>
                optionValue.variant && (this.isSimple || optionValue.bundle)
            )

          const optionMaterials = Shopify.Array.unique(
            optionValues
              .map((v) =>
                this.materials.find((m) => m.key === v.swatch?.material)
              )
              .filter((m) => m)
          ).map((m) => {
            const values = optionValues.filter(
              (v) => v.swatch?.material === m.key
            )
            const offsetPrices = values.map((v) => v.offsetPrice)
            const offsetPriceRange = [
              Math.min(...offsetPrices),
              Math.max(...offsetPrices),
            ]

            return {
              ...m,
              values,
              offsetPriceRange,
              offsetIsRange: !(offsetPriceRange[0] === offsetPriceRange[1]),
              image: values.map((v) => v.swatch).filter((i) => i?.src)[0],
            }
          })

          return {
            name: this.options[i].name,
            values: optionValues,
            materials: optionMaterials,
          }
        }),
      {
        options: this.options,
        validVariants: (this.validVariants ?? []).map((v) => v.id),
        selectedBunlde: this.selectedBundle?.id,
      }
    )
  },

  get unitCompareAtPrice() {
    return this.getBundledPrice(this.selectedVariant, this.selectedBundle)
  },

  get unitDiscount() {
    return this.getBundledDiscount(this.selectedVariant, this.selectedBundle)
  },

  get unitFinalPrice() {
    return this.unitCompareAtPrice - this.unitDiscount
  },

  get totalFinalPrice() {
    return this.unitFinalPrice * this.quantity
  },

  get hasMembershipDiscount() {
    return this.membersDiscountMultiplier < 1
  },

  get totalCompareAtPrice() {
    return this.unitCompareAtPrice * this.quantity
  },

  get hasDiscount() {
    return this.unitDiscount > 0
  },

  get isAvailable() {
    return (
      !!this.selectedVariant?.available &&
      this.allVariants.every((v) => v.available)
    )
  },

  get allVariants() {
    return this.isSimple
      ? [
          {
            ...{},
            ...this.selectedVariant,
            product: this,
          },
        ]
      : [
          ...[],
          ...(this.selectedBundle?.products ?? []).map((product) => ({
            ...{},
            ...product.selectedVariant,
            product,
          })),
        ]
  },

  get variantImages() {
    return this.cache(
      'variantImages',
      () =>
        Shopify.ImagesByVariant(
          this.images,
          this,
          this.selectedVariant,
          this.isSimple ? null : this.selectedBundle
        ),
      {
        options: this.options,
        selectedVariant: this.selectedVariant?.id,
        selectedBundle: this.selectedBundle?.id,
      }
    )
  },

  get galleryImages() {
    const primary = this.primaryVariantImage
    const renderingKeys = ['tag', ...this.options.flatMap(({ name }) => [name, Shopify.slugify(name, '_')])]
    return this.variantImages.filter(image => {
      if (image.attachedToVariant || image.src === primary?.src) return true
      if (typeof image.alt !== 'string' || !/^featured(?:\s*,|$)/i.test(image.alt.trim())) return false
      // Featured lifestyle photos can have captions without belonging to a configuration or material.
      const { objects } = Shopify.ImageAltData(image.alt, this.options)
      return objects.some(metadata => renderingKeys.some(key => !!metadata[key]))
    })
  },

  get lifestyleImages() {
    const renderingSources = new Set(this.galleryImages.map(image => image.src))
    return this.variantImages.filter(image => !renderingSources.has(image.src))
  },

  get isFree() {
    return !this.totalFinalPrice
  },

  get isRestricted() {
    return getStore('cart').restrictedProducts[this.handle]
  },

  get klaviyoFormID() {
    return (
      !this.isAvailable &&
      (this.metafields?.cart.klaviyo_form_id ||
        this.selectedVariant?.metafields?.cart.klaviyo_form_id)
    )
  },

  get outOfStockOptionsPerSelectedOption() {
    return [
      ...new Set(
        this.selectedOptions
          .filter((option) => option !== '')
          .map((option, idx) =>
            this.variants
              .filter(
                (variant) =>
                  variant.options[idx] === option && !variant.available
              )
              .map((variant) => variant.options)
          )
      ),
    ]
  },

  isOneDimensionOptionAvailable(option) {
    if (!option) return false
    return Boolean(
      this.availableVariants.find(
        (variant) =>
          variant &&
          variant.options &&
          variant.options.some(
            (opt) => opt.toUpperCase() === option.toUpperCase()
          )
      )
    )
  },

  get outOfStockOptionsPerSelectedBundle() {
    if (this.isSimple) return []
    return [
      ...new Set(
        this.selectedBundle.products
          .map((product) =>
            product.variants
              .filter(
                (variant) =>
                  !variant.available &&
                  variant.options
                    .filter((opt) => opt !== '')
                    .every((option, idx) =>
                      this.options[idx].values.includes(option)
                    )
              )
              .map((variant) => variant.options.filter((opt) => opt !== ''))
          )
          .flat()
      ),
    ]
  },

  get outOfStockBundlesPerSelectedOptions() {
    if (this.isSimple) return []
    return [
      ...new Set(
        this.selectedOptions
          .filter((option) => option !== '')
          .map((option) =>
            this.bundles
              .filter((bundle) =>
                bundle.products.find((product) =>
                  product.variants.find(
                    (variant) =>
                      !variant.available &&
                      variant.options.every(
                        (opt, _idx) => this.selectedOptions[_idx] === opt
                      ) &&
                      option !== ''
                  )
                )
              )
              .map((bundle) => bundle.id)
          )
      ),
    ]
  },

  isProductOutOfStock(forLoopIndex0, value, optionsLength) {
    if (
      this.isSimple &&
      this.selectedOptions.includes(value) &&
      this.isAvailable
    )
      return false

    if (optionsLength === 1) {
      return !this.isOneDimensionOptionAvailable(value)
    }

    if (!this.isSimple) {
      let isBundleOutOfStock = false
      let isOptionsOutOfStock = false
      if (this.outOfStockBundlesPerSelectedOptions.length) {
        isBundleOutOfStock = this.outOfStockBundlesPerSelectedOptions.every(
          (options) => options.includes(value)
        )
      }

      if (this.outOfStockOptionsPerSelectedBundle.length) {
        isOptionsOutOfStock = this.outOfStockOptionsPerSelectedBundle
          .filter((comb) => comb[forLoopIndex0] === value)
          .some((combination) =>
            combination
              .filter((_, idx) => idx !== forLoopIndex0)
              .every((opt) =>
                this.selectedOptions.find(
                  (selectedOption, idx) =>
                    idx !== forLoopIndex0 && selectedOption === opt
                )
              )
          )
      }

      return isBundleOutOfStock || isOptionsOutOfStock
    }

    return this.outOfStockOptionsPerSelectedOption
      .filter((_, idx) => idx !== forLoopIndex0)
      .flat()
      .filter((opts) => opts.includes(value))
      .some((combination) =>
        combination
          .filter((_, idx) => idx !== forLoopIndex0)
          .every((opt) =>
            this.selectedOptions
              .filter((_, idx) => idx !== forLoopIndex0)
              .includes(opt)
          )
      )
  },

  triggerKlaviyoForm() {
    window._klOnsite = window._klOnsite || []
    window._klOnsite.push(['openForm', this.klaviyoFormID])
  },

  getSwatchThumbnail(optionIndex = 0, optionValue = '') {
    return this.optionsWithSwatches[optionIndex].values.find(
      (v) => v.label === optionValue
    ).swatch
  },

  getFinalBundledPrice(variantOrVariantId, bundleOrBundleId) {
    return (
      this.getBundledPrice(variantOrVariantId, bundleOrBundleId) -
      this.getBundledDiscount(variantOrVariantId, bundleOrBundleId)
    )
  },

  getBundledPrice(variantOrVariantId, bundleOrBundleId) {
    return this.cache(
      'getBundledPrice',
      () => {
        const selectedVariant =
          (typeof variantOrVariantId === 'object'
            ? variantOrVariantId
            : this.variants.find((v) => v.id === this.variantOrVariantId)) ??
          this.selectedVariant
        const bundle =
          typeof bundleOrBundleId === 'object'
            ? bundleOrBundleId
            : this.getBundleWithSelectedVariant(bundleOrBundleId)
        return !selectedVariant
          ? 0
          : this.isSimple
            ? selectedVariant.price
            : !bundle
              ? null
              : bundle.products.reduce(
                  (acc, product) => acc + (product.selectedVariant?.price ?? 0),
                  0
                )
      },
      {
        variantOrVariantId: variantOrVariantId?.id ?? variantOrVariantId,
        bundleOrBundleId: bundleOrBundleId?.id ?? bundleOrBundleId,
        selectedVariant: this.selectedVariant?.id,
      }
    )
  },

  getBundledDiscount(variantOrVariantId, bundleOrBundleId) {
    return this.cache(
      'getBundledDiscount',
      () => {
        const regularPrice = this.getBundledPrice(
          variantOrVariantId,
          bundleOrBundleId
        )

        const selectedVariant = this._resolveVariant(variantOrVariantId)

        const bundle = this._resolveBundle(bundleOrBundleId)

        const productDiscounts = this._calculateProductDiscounts(
          regularPrice,
          bundle
        )

        const variantDiscounts = this._calculateVariantDiscounts(
          regularPrice,
          selectedVariant,
          bundle
        )

        return Math.max(...productDiscounts, ...variantDiscounts)
      },
      {
        variantOrVariantId,
        bundleOrBundleId,
        selectedVariant: this.selectedVariant,
      }
    )
  },

  _resolveVariant(variantOrVariantId) {
    if (typeof variantOrVariantId === 'object') {
      return variantOrVariantId
    }

    if (
      typeof variantOrVariantId === 'number' ||
      typeof variantOrVariantId === 'string'
    ) {
      return (
        this.variants.find((v) => v.id === variantOrVariantId) ??
        this.selectedVariant
      )
    }

    return this.selectedVariant
  },

  _resolveBundle(bundleOrBundleId) {
    if (typeof bundleOrBundleId === 'object') {
      return bundleOrBundleId
    }

    if (bundleOrBundleId) {
      return this.getBundleWithSelectedVariant(bundleOrBundleId)
    }

    return null
  },

  _calculateProductDiscounts(regularPrice, bundle) {
    if (this.isSimple) {
      const usdDiscount = this.discount.usd || 0
      const percentDiscount =
        (regularPrice / 100) * (this.discount.percent || 0)
      return [usdDiscount, percentDiscount]
    }

    if (!bundle) {
      return [0, 0]
    }

    return bundle.products.reduce(
      ([totalUsd, totalPercent], product) => {
        const productUsd = product.discount?.usd || 0
        const productPercent = product.discount?.percent || 0
        const productPrice = product.selectedVariant?.price || 0

        return [
          totalUsd + productUsd,
          totalPercent + (productPrice / 100) * productPercent,
        ]
      },
      [0, 0]
    )
  },

  _calculateVariantDiscounts(regularPrice, selectedVariant, bundle) {
    if (!selectedVariant) {
      return [0, 0]
    }

    if (this.isSimple) {
      const usdDiscount = selectedVariant.discount?.usd || 0
      const percentDiscount =
        regularPrice * (selectedVariant.discount?.percent ?? 0)
      return [usdDiscount, percentDiscount]
    }

    if (!bundle) {
      return [0, 0]
    }

    return bundle.products.reduce(
      ([totalUsd, totalPercent], product) => {
        const variant = product.selectedVariant
        if (!variant) {
          return [totalUsd, totalPercent]
        }

        const variantUsd = variant.discount?.usd || 0
        const variantPercent = variant.discount?.percent || 0
        const variantPrice = variant.price || 0

        return [
          totalUsd + variantUsd,
          totalPercent + variantPrice * variantPercent,
        ]
      },
      [0, 0]
    )
  },

  getBundlePriceOffset(bundleId) {
    return this.cache(
      'getBundlePriceOffset',
      () => {
        const offset =
          this.getFinalBundledPrice(this.selectedVariant, bundleId) -
          this.totalFinalPrice
        return offset
          ? `${offset < 0 ? '-' : '+'}${Shopify.Money(Math.abs(offset))}`
          : null
      },
      {
        selectedVariant: this.selectedVariant,
        price: this.totalFinalPrice,
        bundleId,
      }
    )
  },

  getBundleWithSelectedVariant(bundleId, options) {
    const selectedOptions = options ?? this.selectedOptions

    return this.cache(
      'getBundleWithSelectedVariant',
      () => {
        const bundle = this.bundles.find((b) => b.id === bundleId)
        if (!bundle) return null

        const selectedOptionsByName = this.options.map((option, i) => ({
          name: option.name,
          value: selectedOptions[i],
        }))

        return {
          ...bundle,
          products: bundle.products.map((product) => ({
            ...product,
            selectedVariant: product.variants.find((v) => {
              const variantOptionsByName = product.options.map((option, i) => ({
                name: option.name,
                value: v.options[i],
              }))

              return selectedOptionsByName.every((selectedOption) => {
                const variantOption = variantOptionsByName.find(
                  (o) => o.name === selectedOption.name
                )
                return !variantOption
                  ? true
                  : Shopify.slugify(variantOption?.value) ===
                      Shopify.slugify(selectedOption.value)
              })
            }),
          })),
        }
      },
      {
        bundleId,
        selectedOptions,
      }
    )
  },

  setOption(index, value) {
    this.selectedOptions[index] = value
  },

  selectBundle(bundleId) {
    let selectedBundleId = null

    if (this.isSimple) {
      this.bundle = null
    } else if (this.bundles.length) {
      let defaultBundle =
        (this.isPDP
          ? new URLSearchParams(document.location.search).get('configuration')
          : null) ?? this.bundles[0].id
      if (!defaultBundle || defaultBundle === 'null')
        defaultBundle = this.bundles[0].id
      selectedBundleId = bundleId ?? defaultBundle
      if (!this.bundles.map((b) => b.id).includes(selectedBundleId)) {
        selectedBundleId = null
      }
    }

    this.updateURL({
      configuration: selectedBundleId,
    })

    const selectedBundle = this.isSimple
      ? null
      : this.getBundleWithSelectedVariant(
          selectedBundleId,
          this.selectedOptions
        )
    const selectedBundleOptions = (selectedBundle?.products ?? []).reduce(
      (options, p) => {
        p.options.forEach((o) => {
          options[o.name] = Shopify.Array.intersection(
            options[o.name] ?? o.values,
            o.values
          )
        })
        return options
      },
      {}
    )

    this.selectedBundle = {
      ...{},
      ...selectedBundle,
      options: selectedBundleOptions,
    }

    const variantIsSelected = this.selectedVariantId

    this.validVariants = this.getValidVariants()
    this.availableVariants = this.getAvailableVariants()

    this.selectVariant(this.selectedVariantId)

    if (!variantIsSelected) return this.selectBundle()
  },

  getFirstAvailableVariant() {
    return this.isSimple || !this.bundles.length
      ? this.firstAvailableVariant
      : (this.availableVariants[0] ?? this.firstAvailableVariant)
  },

  getAvailableVariants() {
    const availableVariantsPerBundle = this.cache(
      'availableVariantsPerBundle',
      () => {
        if (this.isSimple) return this.validVariants.filter((v) => v.available)
        return this.bundles.reduce((availableVariants, bundle) => {
          availableVariants[bundle.id] = this.validVariants.filter(
            (v) =>
              v.available &&
              this.getBundleWithSelectedVariant(bundle.id, v.options)
                ?.products.map((p) => p.selectedVariant)
                .every((v) => v?.available)
          )
          return availableVariants
        }, {})
      }
    )

    return this.isSimple
      ? availableVariantsPerBundle
      : availableVariantsPerBundle[
          this.selectedBundle?.id ?? this.bundles[0]?.id
        ]
  },

  getCurrentVariantUrl() {
    const _urlParams = this.urlParams.variant
      ? this.urlParams
      : { variant: this.selectedVariantId }

    return Shopify.URL.withSearchParams(
      window.location.origin + '/products/' + this.handle,
      _urlParams
    )
  },

  getValidVariants() {
    return this.isSimple
      ? this.variants
      : this.variants.filter((v) =>
          this.options.every((o, i) => this.optionIsValid(o.name, v.options[i]))
        )
  },

  selectVariant(variantId) {
    if (!!variantId && this.selectedVariantId === variantId) return
    this.selectedVariantId =
      variantId ??
      (this.isPDP
        ? new URLSearchParams(document.location.search).get('variant')
        : null) ??
      this.getFirstAvailableVariant()?.id
  },

  optionIsValid(optionName, _optionValue, decode = false) {
    return this.cache(
      'optionIsValid',
      () => {
        const optionValue = decode ? Shopify.uscape(_optionValue) : _optionValue
        optionIndex = this.options.map((o) => o.name).indexOf(optionName)
        const isValid = !!this.validVariants.find((v) =>
          v.options.every((o, i) => {
            return i === optionIndex
              ? o == optionValue
              : this.selectedOptions[i] == o
          })
        )
        return this.isSimple
          ? isValid
          : !this.selectedBundle?.options[optionName] ||
              this.selectedBundle?.options[optionName].includes(
                Shopify.uscape(optionValue, true)
              )
      },
      {
        optionName,
        _optionValue,
        decode,
      }
    )
  },

  get stagedCount() {
    return (this.stagedAddons ?? []).reduce((n, i) => n + (i.quantity || 1), 0)
  },

  get hasStagedAddons() {
    return (this.stagedAddons ?? []).length > 0
  },

  get stagedFinalTotal() {
    return (this.stagedAddons ?? []).reduce(
      (t, i) => t + (i.unitFinal ?? 0) * (i.quantity || 1),
      0
    )
  },

  get stagedCompareTotal() {
    return (this.stagedAddons ?? []).reduce(
      (t, i) => t + (i.unitCompare ?? 0) * (i.quantity || 1),
      0
    )
  },

  get stagedMainProspectivePercent() {
    return Math.max(
      0,
      ...(this.stagedAddons ?? []).map((i) => i.mainPercent ?? 0)
    )
  },

  get combinedFinalPrice() {
    if (!this.hasStagedAddons) return this.totalFinalPrice
    return (
      this.discountedPriceForVariant(
        this.selectedVariant,
        this.stagedMainProspectivePercent
      ) *
        this.quantity +
      this.stagedFinalTotal
    )
  },

  get combinedCompareAtPrice() {
    return this.totalCompareAtPrice + this.stagedCompareTotal
  },

  get displayFinalPrice() {
    return this.hasStagedAddons ? this.combinedFinalPrice : this.totalFinalPrice
  },

  get displayCompareAtPrice() {
    return this.hasStagedAddons
      ? this.combinedCompareAtPrice
      : this.totalCompareAtPrice
  },

  get displaySavings() {
    return this.displayCompareAtPrice - this.displayFinalPrice
  },

  stagedQtyForHandle(handle) {
    return (this.stagedAddons ?? [])
      .filter((i) => i.handle === handle)
      .reduce((n, i) => n + (i.quantity || 1), 0)
  },

  get headerSummary() {
    let stagedQty = 0
    try {
      const p = typeof this.parent === 'function' ? this.parent() : null
      stagedQty = p ? p.stagedQtyForHandle(this.handle) : 0
    } catch (e) {
      stagedQty = 0
    }
    const firstOpt = (this.selectedOptions ?? []).find(
      (o) => o && `${o}`.trim() !== ''
    )
    return (
      `[${stagedQty}]` +
      (stagedQty > 0 && firstOpt ? ` ${`${firstOpt}`.toUpperCase()}` : '')
    )
  },

  get primaryVariantImage() {
    const imgs = this.variantImages ?? []
    return (
      imgs.find(
        (i) =>
          i.attachedToVariant &&
          (i.variantsIds ?? []).includes(this.selectedVariant?.id)
      ) ??
      imgs[0] ??
      null
    )
  },

  prospectiveBundlePercentFor(variant) {
    if (!variant) return 0
    return Math.max(
      variant.activeBundleDiscountPercent ?? 0,
      this.stagedInBundle ? this.stagedBundleDiscount ?? 0 : 0
    )
  },

  effectiveDiscountForVariant(variant, extraProspective = 0) {
    if (!variant) return 0
    let cap = this.maximumBundleDiscountPercent
    if (!(cap > 0)) cap = 100
    cap = cap / 100
    const pct = Math.max(
      variant.membersDiscountPercent ?? 0,
      this.prospectiveBundlePercentFor(variant),
      extraProspective ?? 0,
      variant.discount?.percent ?? 0,
      (this.discount?.percent ?? 0) / 100
    )
    return Math.min(pct, cap)
  },

  discountedPriceForVariant(variant, extraProspective = 0) {
    if (!variant?.price) return 0
    const byPercent = Math.round(
      variant.price *
        (1 - this.effectiveDiscountForVariant(variant, extraProspective))
    )
    const usd = Math.max(
      variant.discount?.usd || 0,
      this.discount?.usd || 0
    )
    return Math.min(byPercent, Math.max(0, variant.price - usd))
  },

  get stagedUnitFinal() {
    return this.discountedPriceForVariant(this.selectedVariant)
  },

  get stagedUnitCompare() {
    return this.selectedVariant?.price ?? 0
  },

  get fromComparePrice() {
    if (!this.variants?.length) return 0
    return Math.min(...this.variants.map((v) => v.price ?? Infinity))
  },

  get fromFinalPrice() {
    const priced = (this.variants ?? []).filter((v) => v.available)
    const pool = priced.length ? priced : (this.variants ?? [])
    if (!pool.length) return 0
    return Math.min(...pool.map((v) => this.discountedPriceForVariant(v)))
  },

  optionValueOffset(optionIndex) {
    const pool = (this.variants ?? []).filter((v) => v.available)
    if (!pool.length) return 0
    const selected = this.selectedOptions ?? []
    const matchesExcept = (v, ignoreIdx) =>
      (v.options ?? []).every((o, i) => {
        if (i === ignoreIdx) return true
        const sel = selected[i]
        if (!sel) return true
        return o === sel
      })
    const others = pool.filter((v) => matchesExcept(v, optionIndex))
    const withThis = pool.filter(
      (v) =>
        matchesExcept(v, -1) && v.options[optionIndex] === selected[optionIndex]
    )
    if (!others.length || !withThis.length) return 0
    return (
      Math.min(...withThis.map((v) => v.price ?? Infinity)) -
      Math.min(...others.map((v) => v.price ?? Infinity))
    )
  },

  toggleStaged() {
    if (typeof this.parent !== 'function') return
    if (this.isHandleStaged) this.parent().unstageAddon(this.handle)
    else this.stageCurrentSelection()
  },

  stageCurrentSelection() {
    if (typeof this.parent !== 'function') return
    if (!this.selectedVariant?.available) return
    this.parent().stageAddon({
      handle: this.handle,
      title: this.title,
      variantId: this.selectedVariant.id,
      variantTitle: this.selectedVariant.title,
      quantity: parseInt(this.quantity ?? 1, 10) || 1,
      unitFinal: this.stagedUnitFinal,
      unitCompare: this.stagedUnitCompare,
      mainPercent: this.stagedInBundle
        ? (this.stagedBundleDiscount ?? 0)
        : 0,
      image: this.variantImages?.[0]?.src ?? null,
    })
  },

  stageAddon(item) {
    const entry = { key: `${item.handle}`, ...item }
    const idx = (this.stagedAddons ?? []).findIndex(
      (i) => i.handle === item.handle
    )
    if (idx >= 0) {
      this.stagedAddons = [
        ...this.stagedAddons.slice(0, idx),
        entry,
        ...this.stagedAddons.slice(idx + 1),
      ]
    } else {
      this.stagedAddons = [...(this.stagedAddons ?? []), entry]
    }
  },

  unstageAddon(key) {
    this.stagedAddons = (this.stagedAddons ?? []).filter(
      (i) => i.key !== key && i.handle !== key
    )
  },

  get isHandleStaged() {
    try {
      const p = typeof this.parent === 'function' ? this.parent() : null
      if (!p) return false
      return (p.stagedAddons ?? []).some((i) => i.handle === this.handle)
    } catch (e) {
      return false
    }
  },

  syncStaged() {
    if (typeof this.parent !== 'function') return
    const parent = this.parent()
    if (!parent) return
    const entry = (parent.stagedAddons ?? []).find(
      (i) => i.handle === this.handle
    )
    if (!entry || !this.selectedVariant) return
    entry.variantId = this.selectedVariant.id
    entry.variantTitle = this.selectedVariant.title
    entry.quantity = parseInt(this.quantity ?? 1, 10) || 1
    entry.unitFinal = this.stagedUnitFinal
    entry.unitCompare = this.stagedUnitCompare
    entry.mainPercent = this.stagedInBundle
      ? (this.stagedBundleDiscount ?? 0)
      : 0
    entry.image = this.variantImages?.[0]?.src ?? entry.image
    parent.stagedAddons = [...parent.stagedAddons]
  },

  clearStagedAddons() {
    this.stagedAddons = []
  },

  async addToCart() {
    this.isLoading = true
    const cart = getStore('cart')

    const stagedItems = (this.stagedAddons ?? []).map((i) => ({
      id: i.variantId,
      quantity: i.quantity,
      properties: {
        handle: this.getCurrentVariantUrl(),
        _staged_addon_for: this.handle,
      },
    }))

    await cart.add([
      ...this.allVariants.map((v) => ({
        id: v.id,
        quantity: this.quantity,
        properties: {
          handle: this.getCurrentVariantUrl(),
        },
        product: {
          metafields: v.product.metafields,
        },
      })),
      ...stagedItems,
    ])

    cart.toggleMinicart(true)
    this.isLoading = false
  },

  updateURL(params = {}) {
    if (!this.isPDP) return

    this.urlParams = {
      ...this.urlParams,
      ...params,
    }

    clearTimeout(this.updateURLTimeout)

    this.updateURLTimeout = setTimeout(() => {
      history.replaceState({}, '', this.getCurrentVariantUrl())
    }, 50)
  },

  updateVariantFromExternalOptions(externalOptions) {
    this.selectedOptions = [0, 1, 2].map((optionIndex) => {
      const currentOption = this.options[optionIndex]

      if (!currentOption) {
        return this.selectedOptions[optionIndex]
      }

      const matchingExternalOption = externalOptions.find((externalOption) => {
        if (externalOption.name !== currentOption.name) {
          return false
        }

        return currentOption.values.some((availableValue) => {
          const externalValue = externalOption.value.toLowerCase()
          const currentValue = availableValue.toLowerCase()

          return (
            externalValue.includes(currentValue) ||
            currentValue.includes(externalValue) ||
            externalValue === currentValue
          )
        })
      })

      if (matchingExternalOption) {
        const exactMatch = currentOption.values.find(
          (value) =>
            value.toLowerCase() === matchingExternalOption.value.toLowerCase()
        )

        return (
          exactMatch ||
          currentOption.values.find((value) => {
            const externalValue = matchingExternalOption.value.toLowerCase()
            const currentValue = value.toLowerCase()
            return (
              externalValue.includes(currentValue) ||
              currentValue.includes(externalValue)
            )
          })
        )
      }

      return this.selectedOptions[optionIndex]
    })

    this.selectBundle()
    if (typeof this.syncStaged === 'function') this.syncStaged()
  },

  onScroll() {
    this.atcBox = document
      .querySelectorAll('.main-product__col--info .product-form__buttons')[0]
      .getBoundingClientRect()
  },

  _selectFirstAvailableBundleConfiguration() {
    if (this.bundles.length && !new URL(document.location).searchParams.size) {
      const firstAvailableConfiguration = this.bundles
        .map((bundle) => {
          const firstAvailableVariant = bundle.products
            .flatMap((product) =>
              product.variants.find(
                (variant) =>
                  variant.available &&
                  variant.options.filter((it) => it).length ===
                    this.options.length
              )
            )
            .filter((it) => it)[0]

          if (!firstAvailableVariant) return

          return {
            bundleId: bundle.id,
            options: firstAvailableVariant.options,
          }
        })
        .filter((it) => it)[0]

      if (firstAvailableConfiguration) {
        setTimeout(
          () => this.selectBundle(firstAvailableConfiguration.bundleId),
          300
        )
        setTimeout(
          () =>
            firstAvailableConfiguration.options.map((option, idx) => {
              this.selectedOptions[idx] = option
            }),
          500
        )
      }
    }
  },

  async hydrateProduct() {
    if (!this.handle || this.isHydrated !== null) return
    this.isHydrated = false

    const product = await getStore('products').fetchProduct(this.handle)

    PRODUCT_PROPERTIES.forEach((key) => {
      this[key] = product[key]
    })

    this.isHydrated = true
    if (this.isPDP) window.dispatchEvent(new CustomEvent('floyd:product:hydrated'))
  },

  get atcLabel() {
    if (this.isFree) return ''
    return `${
      this.unitDiscount
        ? `<span class="compare-at-price">${Shopify.Money(
            this.totalCompareAtPrice
          )} Comp</span> `
        : ''
    }${Shopify.Money(this.totalFinalPrice)}`
  },

  get atcLabelReversed() {
    if (this.isFree) return ''
    return `${Shopify.Money(this.totalFinalPrice)}${
      this.unitDiscount
        ? ` <span class="compare-at-price">${Shopify.Money(
            this.totalCompareAtPrice
          )} Comp</span>`
        : ''
    }`
  },

  ...(config ?? {}),
})

/*
 * PDP store
 */
const productStore = createStore(
  'product',
  ProductState({
    isPDP: true,
  })
)

/*
 * Product component
 */
createComponent('Product', function (_config) {
  let localState = {}

  const config = _config ? Shopify.uscape(_config) : productStore
  let store = null

  if (_config) {
    if (config.isSimple) config.bundles = []
    config.isSimple = !config.bundles.length

    // Bootstrap global store if PDP
    if (config?.isPDP) {
      Object.keys(config).forEach((key) => {
        productStore[key] = config[key]
      })
      store = productStore
      productStore.onScroll()
      this.viewport.addListener('scroll', () => productStore.onScroll(), {
        throttle: 200,
      })
    }
    // Add local state instead
    else {
      localState.id = config.id
      localState.product = ProductState(config)
      store = localState.product
    }

    store?.selectBundle()
  }

  return {
    ...localState,

    headerHeight: '95px',

    mount(el) {
      this.product.hydrateProduct()
      this.bindATCForm(el)
      this.headerHeight =
        document.querySelector('#shopify-section-header').offsetHeight +
        document.querySelector('#shopify-section-header').offsetTop +
        'px'
      this.product._selectFirstAvailableBundleConfiguration()
    },

    bindATCForm(el) {
      el.querySelector(
        `form[data-type="add-to-cart-form"][data-product-id="${config.id}"]`
      )?.addEventListener('submit', (ev) => {
        ev.preventDefault()
        this.product.addToCart()
        return false
      })
    },
  }
})
